import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, imgUrl, rupee } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, ShoppingBag, ArrowLeft, Lock, RefreshCw, Headphones } from "lucide-react";
import { toast } from "sonner";

const Cart = () => {
  const { cart, updateQty, removeFromCart, cartSubtotal } = useStore();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [applied, setApplied] = useState("");

  const applyCoupon = async () => {
    try {
      const r = await api.post("/coupons/apply", { code, subtotal: cartSubtotal });
      setDiscount(r.data.discount); setApplied(r.data.code); toast.success(`Coupon ${r.data.code} applied`);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid coupon"); setDiscount(0); setApplied(""); }
  };

  const shipping = cartSubtotal >= 999 ? 0 : (cartSubtotal > 0 ? 79 : 0);
  const total = cartSubtotal - discount + shipping;

  if (cart.length === 0) return (
    <div className="flex flex-col items-center gap-4 py-32 text-center" data-testid="empty-cart">
      <ShoppingBag className="h-14 w-14 text-muted-text/40" />
      <h1 className="font-serif text-3xl text-charcoal">Your cart is empty</h1>
      <p className="text-muted-text">Looks like you haven't added anything yet.</p>
      <Button asChild className="rounded-none bg-burgundy"><Link to="/new-arrivals">Start Shopping</Link></Button>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:px-6">
      <h1 className="font-serif text-4xl text-burgundy">Your Cart ({cart.length})</h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="border border-border">
          {cart.map((i) => (
            <div key={i.key} className="flex gap-4 border-b border-border p-4 last:border-0" data-testid={`cart-row-${i.slug}`}>
              <img src={imgUrl(i.image)} alt={i.name} className="h-28 w-24 object-cover" />
              <div className="flex flex-1 flex-col">
                <div className="flex justify-between">
                  <Link to={`/product/${i.slug}`} className="font-medium hover:text-burgundy">{i.name}</Link>
                  <button onClick={() => removeFromCart(i.key)} data-testid={`cart-del-${i.slug}`}><Trash2 className="h-4 w-4 text-muted-text hover:text-burgundy" /></button>
                </div>
                <p className="text-xs text-muted-text">{[i.size && `Size: ${i.size}`, i.color && `Color: ${i.color}`].filter(Boolean).join(" | ")}</p>
                <p className="mt-1 font-semibold text-burgundy">{rupee(i.price)}</p>
                <div className="mt-auto flex items-center gap-2">
                  <button onClick={() => updateQty(i.key, i.quantity - 1)} className="h-8 w-8 border border-border">-</button>
                  <span className="w-8 text-center">{i.quantity}</span>
                  <button onClick={() => updateQty(i.key, i.quantity + 1)} className="h-8 w-8 border border-border">+</button>
                  <span className="ml-auto font-medium">{rupee(i.price * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="h-fit border border-border p-6">
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter coupon code" data-testid="coupon-input" className="rounded-none" />
            <Button onClick={applyCoupon} variant="outline" className="rounded-none border-burgundy text-burgundy" data-testid="apply-coupon">Apply</Button>
          </div>
          <p className="mt-2 text-xs text-muted-text">Try WELCOME10 or FLAT200</p>
          <div className="mt-6 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{rupee(cartSubtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-green-600"><span>Discount ({applied})</span><span>-{rupee(discount)}</span></div>}
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : rupee(shipping)}</span></div>
            <div className="mt-2 flex justify-between border-t border-border pt-3 font-serif text-lg text-burgundy"><span>Total</span><span>{rupee(total)}</span></div>
          </div>
          <Button onClick={() => navigate("/checkout")} data-testid="proceed-checkout" className="mt-6 w-full rounded-none bg-burgundy uppercase tracking-widest">Proceed to Checkout</Button>
          <Link to="/new-arrivals" className="mt-4 flex items-center justify-center gap-1 text-sm text-muted-text hover:text-burgundy"><ArrowLeft className="h-4 w-4" /> Continue Shopping</Link>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-3 gap-4 border-t border-border pt-8 text-center text-xs text-muted-text">
        <div className="flex flex-col items-center gap-1"><Lock className="h-5 w-5 text-burgundy" />Secure Checkout</div>
        <div className="flex flex-col items-center gap-1"><RefreshCw className="h-5 w-5 text-burgundy" />Easy Returns</div>
        <div className="flex flex-col items-center gap-1"><Headphones className="h-5 w-5 text-burgundy" />24/7 Support</div>
      </div>
    </div>
  );
};

export default Cart;
